import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Ruler, Home, Calendar, FileText, StickyNote, Upload, Image as ImageIcon, Mail, CheckSquare, Share2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { StatusBadge } from "../components/Layout";
import { OpportunityScore, InvestorMatches } from "../components/OpportunityScore";
import PhotoGallery from "../components/PhotoGallery";
import EmailDraftDialog from "../components/EmailDraftDialog";
import DocChecklist from "../components/DocChecklist";
import TasksBoard from "../components/TasksBoard";
import DealRoomManager from "../components/DealRoomManager";
import SignatureCard from "../components/SignatureCard";
import AttachmentsUnlock from "../components/AttachmentsUnlock";
import { useAuth } from "../context/AuthContext";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const canDelete = user && user.role !== "client";
  const [prop, setProp] = useState(null);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [activities, setActivities] = useState([]);
  const [note, setNote] = useState("");
  const [emailContact, setEmailContact] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef();

  const removeProperty = async () => {
    setDeleting(true);
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Immobile eliminato");
      navigate("/properties");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const load = async () => {
    if (isClient) {
      const p = await api.get(`/properties/${id}`);
      setProp(p.data);
      return;
    }
    const [p, n, f, a] = await Promise.all([
      api.get(`/properties/${id}`),
      api.get(`/notes`, { params: { entity_type: "property", entity_id: id } }),
      api.get(`/uploads`, { params: { entity_type: "property", entity_id: id } }),
      api.get(`/activities`, { params: { entity_type: "property", entity_id: id } }),
    ]);
    setProp(p.data); setNotes(n.data); setFiles(f.data); setActivities(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post("/notes", { entity_type: "property", entity_id: id, content: note });
      setNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", "property");
    fd.append("entity_id", id);
    try {
      await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File caricato");
      e.target.value = ""; load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!prop) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6" data-testid="property-detail">
      <div className="flex items-center justify-between">
        <Link to="/properties" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Torna agli immobili
        </Link>
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive" data-testid="delete-property-button">
                <Trash2 className="w-4 h-4" /> Elimina immobile
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-property-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare l'immobile?</AlertDialogTitle>
                <AlertDialogDescription>
                  Stai per eliminare <strong className="text-foreground">{prop.title}</strong>. Verranno rimossi anche foto, documenti e attività collegate. Questa azione non è reversibile.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={removeProperty}
                  disabled={deleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="delete-confirm"
                >
                  {deleting ? "Eliminazione…" : "Elimina definitivamente"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Signature — the product's opinionated valuation */}
      <SignatureCard propertyId={id} />

      {/* Client-only: Stripe unlock for attachments */}
      {isClient && <AttachmentsUnlock propertyId={id} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-border/60 overflow-hidden">
            <div className="h-64 md:h-80 bg-muted relative">
              {prop.hero_image ? (
                <img src={prop.hero_image.startsWith("http") ? prop.hero_image : `${API}${prop.hero_image}`}
                  alt={prop.title} className="w-full h-full object-cover" />
              ) : <div className="flex h-full items-center justify-center text-muted-foreground"><Home className="w-12 h-12" strokeWidth={1} /></div>}
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">{prop.title}</h1>
                  <div className="text-muted-foreground text-sm flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{prop.address}{prop.city ? `, ${prop.city}` : ""}{prop.province ? ` (${prop.province})` : ""}</div>
                </div>
                <StatusBadge status={prop.status} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <Stat label="Prezzo" value={`€ ${Intl.NumberFormat("it-IT").format(prop.price || 0)}`} />
                <Stat label="Valore stimato" value={`€ ${Intl.NumberFormat("it-IT").format(prop.estimated_value || 0)}`} />
                <Stat label="Superficie" value={`${prop.size_sqm || 0} mq`} icon={Ruler} />
                <Stat label="Locali" value={`${prop.rooms || 0} · ${prop.bathrooms || 0} bagni`} />
              </div>
              {prop.description && <p className="text-sm text-muted-foreground leading-relaxed mt-6">{prop.description}</p>}
              {prop.tags?.length > 0 && <div className="flex gap-1.5 flex-wrap mt-4">{prop.tags.map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}</div>}
            </div>
          </Card>

          <Card className="border-border/60 p-6">
            <Tabs defaultValue="photos">
              <TabsList>
                <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon className="w-4 h-4 mr-1.5" />Foto</TabsTrigger>
                {!isClient && <TabsTrigger value="tasks" data-testid="tab-tasks"><CheckSquare className="w-4 h-4 mr-1.5" />Attività</TabsTrigger>}
                {!isClient && <TabsTrigger value="notes" data-testid="tab-notes"><StickyNote className="w-4 h-4 mr-1.5" />Note</TabsTrigger>}
                {!isClient && <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1.5" />Documenti</TabsTrigger>}
                {!isClient && <TabsTrigger value="dealroom" data-testid="tab-dealroom"><Share2 className="w-4 h-4 mr-1.5" />Deal Room</TabsTrigger>}
                {!isClient && <TabsTrigger value="timeline" data-testid="tab-timeline"><Calendar className="w-4 h-4 mr-1.5" />Timeline</TabsTrigger>}
              </TabsList>
              <TabsContent value="photos" className="mt-4">
                <PhotoGallery propertyId={id} readOnly={isClient} />
              </TabsContent>
              {!isClient && <>
              <TabsContent value="tasks" className="mt-4">
                <TasksBoard propertyId={id} />
              </TabsContent>
              <TabsContent value="notes" className="mt-4 space-y-3">
                <div className="flex gap-2">
                  <Textarea placeholder="Aggiungi una nota…" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="note-textarea" />
                  <Button onClick={addNote} data-testid="add-note-button">Aggiungi</Button>
                </div>
                <div className="space-y-2 mt-4">
                  {notes.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessuna nota ancora.</p>}
                  {notes.map((n) => (
                    <div key={n.id} className="border border-border/60 rounded-md p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{n.author_name}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">{new Date(n.created_at).toLocaleString("it-IT")}</span>
                      </div>
                      <p className="text-muted-foreground whitespace-pre-wrap">{n.content}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="documents" className="mt-4 space-y-3">
                <div>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={upload} data-testid="file-input" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-2" data-testid="upload-file-button"><Upload className="w-4 h-4" /> Carica documento</Button>
                </div>
                <div className="space-y-2 mt-4">
                  {files.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessun documento caricato.</p>}
                  {files.map((f) => (
                    <a key={f.id} href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-3 p-3 rounded-md border border-border/60 hover:bg-muted/50 transition-colors">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm flex-1 truncate">{f.original_filename}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">{Math.round((f.size || 0) / 1024)} KB</span>
                    </a>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="dealroom" className="mt-4">
                <DealRoomManager propertyId={id} />
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <div className="relative pl-6 space-y-4">
                  {activities.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessuna attività registrata.</p>}
                  {activities.map((a) => (
                    <div key={a.id} className="relative">
                      <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-primary ring-4 ring-primary/10" />
                      <div className="text-sm"><span className="font-medium">{a.user_name}</span> <span className="text-muted-foreground">{a.action}</span></div>
                      {a.detail && <p className="text-xs text-muted-foreground mt-0.5">{a.detail}</p>}
                      <p className="text-xs text-muted-foreground/70 tabular-nums mt-0.5">{new Date(a.created_at).toLocaleString("it-IT")}</p>
                    </div>
                  ))}
                </div>
              </TabsContent>
              </>}
            </Tabs>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <OpportunityScore propertyId={id} />
          {!isClient && <DocChecklist propertyId={id} />}
          {!isClient && <InvestorMatches propertyId={id} onEmail={(c) => setEmailContact(c)} />}
          <Card className="border-border/60 p-6">
            <h3 className="font-heading font-medium mb-4">Dettagli</h3>
            <dl className="text-sm space-y-3">
              <Row k="Tipologia" v={prop.property_type} />
              <Row k="Anno" v={prop.year_built || "—"} />
              <Row k="Città" v={prop.city || "—"} />
              <Row k="Provincia" v={prop.province || "—"} />
              <Row k="Paese" v={prop.country || "—"} />
              <Row k="Creato" v={new Date(prop.created_at).toLocaleDateString("it-IT")} />
            </dl>
          </Card>
        </div>
      </div>
      <EmailDraftDialog
        open={!!emailContact}
        onOpenChange={(v) => !v && setEmailContact(null)}
        contact={emailContact}
        propertyId={id}
        onLogged={load}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{label}</div>
      <div className="font-heading text-lg font-semibold tabular-nums mt-1">{value}</div>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between gap-2"><dt className="text-muted-foreground">{k}</dt><dd className="text-foreground capitalize text-right">{v}</dd></div>;
}
